import qs from 'qs';

import type {
  CharterPromotionPricingInventoryApiRow,
  CharterPromotionPricingInventoryRow,
  ListCharterPromotionPricingInventoryParams,
  ListCharterPromotionPricingInventoryResponse,
  UpsertCharterPromotionPricingInventoryBody,
  UpsertCharterPromotionPricingInventoryResponse,
} from '~/types/charterPromotionPricingInventory';

import { json_headers, request } from './common';

const BASE_PATH = `${window.configs.API_HOST}/api/flights-charter/v1/fare-families/promotions/hundred-dollar`;

type RequestOptions = {
  signal?: AbortSignal;
};

const contractToUiCabinMap = {
  ECONOMY: 'economy',
} as const;

function toUiCabinClass(cabinClass: string): CharterPromotionPricingInventoryRow['cabin'] {
  return contractToUiCabinMap[cabinClass as keyof typeof contractToUiCabinMap] ?? 'economy';
}

function getPricingPrice(
  pricing: Array<CharterPromotionPricingInventoryApiRow['pricing'][number]>,
  paxType: 'ADT' | 'CHD',
): number {
  return pricing.find((pricingRow) => pricingRow.paxType === paxType)?.price ?? 0;
}

function toCharterPromotionPricingInventoryRow(
  pricingInventory: CharterPromotionPricingInventoryApiRow,
): CharterPromotionPricingInventoryRow {
  const depAirport = pricingInventory.flightInstance?.depAirport ?? '';
  const arrAirport = pricingInventory.flightInstance?.arrAirport ?? '';

  return {
    id: pricingInventory.promotionFareId,
    promotionType: pricingInventory.promotionType,
    flightInstanceId: pricingInventory.flightInstanceId,
    depAirport,
    arrAirport,
    route: depAirport && arrAirport ? `${depAirport} -> ${arrAirport}` : '',
    date: pricingInventory.flightInstance?.depDate ?? '',
    cabin: toUiCabinClass(pricingInventory.cabinClass),
    availableDaysBefore: pricingInventory.availableDaysBefore,
    promotionAllocation: pricingInventory.allocation,
    totalBooked: pricingInventory.booked,
    adultPrice: getPricingPrice(pricingInventory.pricing, 'ADT'),
    childPrice: getPricingPrice(pricingInventory.pricing, 'CHD'),
    openForSale: pricingInventory.allocation - pricingInventory.booked,
  };
}

export async function listCharterHundredDollarPromotion(
  params: ListCharterPromotionPricingInventoryParams = {},
  options: RequestOptions = {},
): Promise<ListCharterPromotionPricingInventoryResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  const url = query ? `${BASE_PATH}?${query}` : BASE_PATH;

  const response: {
    status: number;
    message: string;
    result: Array<CharterPromotionPricingInventoryApiRow>;
  } = await request(url, {
    ...options,
    method: 'GET',
  });

  return {
    status: response.status,
    message: response.message,
    result: response.result.map((pricingInventory) => toCharterPromotionPricingInventoryRow(pricingInventory)),
  };
}

export async function upsertCharterHundredDollarPromotion(
  body: UpsertCharterPromotionPricingInventoryBody,
): Promise<UpsertCharterPromotionPricingInventoryResponse> {
  const response: {
    status: number;
    message: string;
    result: CharterPromotionPricingInventoryApiRow;
  } = await request(BASE_PATH, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(body),
  });

  return {
    status: response.status,
    message: response.message,
    result: toCharterPromotionPricingInventoryRow(response.result),
  };
}
